import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SyncInfo {
  sync_type: string;
  completed_at: string | null;
  error_message: string | null;
  records_synced: number | null;
}

export function useLastSync(syncType: string) {
  return useQuery({
    queryKey: ['last-sync', syncType],
    queryFn: async (): Promise<SyncInfo | null> => {
      const { data, error } = await (supabase as any)
        .from('sync_log')
        .select('sync_type, completed_at, error_message, records_synced')
        .eq('sync_type', syncType)
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      if (error) return null;
      return data as SyncInfo;
    },
    refetchInterval: 5 * 60 * 1000, // refresh every 5 min
  });
}

export function formatSyncAge(completedAt: string | null): { label: string; isStale: boolean } {
  if (!completedAt) return { label: 'Never synced', isStale: true };

  const now = new Date();
  const syncDate = new Date(completedAt);
  const diffMs = now.getTime() - syncDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let label: string;
  if (diffMins < 1) label = 'Just now';
  else if (diffMins < 60) label = `${diffMins}m ago`;
  else if (diffHours < 24) label = `${diffHours}h ago`;
  else label = `${diffDays}d ago`;

  return { label, isStale: diffDays >= 3 };
}
